import { useState } from 'react';
import { API_URL, API_VERSION, SITE_URLS } from '@constants';
import { useData } from '@hooks/useData';
import { paths } from '../../types/api';
import { ApiError, useApiConfig } from '@hooks/useApiConfig';
import { useCustomNavigate } from '@hooks/useCustomNavigate';

const ENDPOINT_URL = '/prd-attributes/category/{uuid}/all';

const ENDPOINT_URL_WITH_VERSION = `${API_VERSION}${ENDPOINT_URL}` as const;
type url = typeof ENDPOINT_URL_WITH_VERSION;

export type GetAllProductAttributesByCategoryInput = paths[url]['get']['parameters']['path'] &
    paths[url]['get']['parameters']['query'];

export interface GetAllProductAttributesByCategorySuccessResponse {
    data: paths[url]['get']['responses']['200']['content']['application/json'];
}

/**
 * Requests endpoint to get all products' attributes from a product category.
 */
export const useGetAllProductAttributesByCategory = () => {
    const { navigate } = useCustomNavigate();
    const { getHeaders, getCommonParameters, handleCommonErrors } = useApiConfig();
    const { setAllProductAttributesByCategory } = useData();
    const [loading, setLoading] = useState<boolean>(false);

    const request = async (props: GetAllProductAttributesByCategoryInput) => {
        setLoading(true);

        try {
            const inputParameters = new URLSearchParams({ ...getCommonParameters(), ...props }).toString();

            const response = await fetch(`${API_URL}${ENDPOINT_URL.replace('{uuid}', props.uuid || '')}?${inputParameters}`, {
                method: 'GET',
                headers: getHeaders(),
            });

            if (!response.ok) {
                const json = await response.json();
                const error = new Error(json.error || '') as ApiError;
                error.statusCode = response.status;
                throw error;
            }

            const data: GetAllProductAttributesByCategorySuccessResponse = await response.json();

            setAllProductAttributesByCategory(data);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (error: any) {
            handleCommonErrors(error);

            if (error.statusCode === 404) {
                // redirect to 404 page
                navigate(`/${SITE_URLS.pageNotFound}`);
            }
        } finally {
            setLoading(false);
        }
    };

    return { request, loading };
};
